import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const HERO =
  "https://images.unsplash.com/photo-1648197752056-86e65cbfe0cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxjYXR0bGUlMjBwYXN0dXJlfGVufDB8fHx8MTc4NTg3NTI1NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      toast.success("Bem-vindo ao GadoPro");
      navigate("/app", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível entrar");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-gado-cream">
      {/* Left — form */}
      <div className="flex flex-col px-6 md:px-16 py-8">
        <Link
          to="/"
          data-testid="back-home-link"
          className="inline-flex items-center gap-2 text-gado-muted hover:text-gado-green transition-colors text-sm"
        >
          <ArrowLeft size={16} /> Voltar ao site
        </Link>

        <div className="flex-1 flex flex-col justify-center max-w-md w-full mx-auto py-10">
          <div className="flex items-center gap-3 mb-10">
            <img src="/logo-128.png" alt="GadoPro" className="h-11 w-11 rounded-md object-contain" />
            <div className="leading-tight">
              <div className="font-cabinet font-extrabold text-2xl text-gado-ink">
                Gado<span className="text-gado-gold">Pro</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-gado-muted">
                Gestão Inteligente de Pesagens
              </div>
            </div>
          </div>

          <motion.div
            key={mode}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <h1 className="font-cabinet text-3xl font-bold text-gado-ink tracking-tight">
              {mode === "login" ? "Entrar na conta" : "Criar sua conta"}
            </h1>
            <p className="text-gado-muted mt-2 mb-8">
              {mode === "login"
                ? "Acesse suas pesagens e relatórios."
                : "Comece a digitalizar suas compras de gado."}
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
              {mode === "register" && (
                <div>
                  <Label htmlFor="name">Nome</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Seu nome"
                    className="mt-1.5"
                    required
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="voce@fazenda.com"
                  className="mt-1.5"
                  required
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="password">Senha</Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="password-input"
                  value={form.password}
                  onChange={set("password")}
                  placeholder="••••••"
                  className="mt-1.5"
                  required
                  autoComplete={mode === "register" ? "new-password" : "current-password"}
                />
              </div>

              <Button
                type="submit"
                data-testid="submit-auth-button"
                disabled={loading}
                className="w-full bg-gado-green hover:bg-gado-greenHover text-white h-11"
              >
                {loading
                  ? "Aguarde…"
                  : mode === "login"
                  ? "Entrar"
                  : "Criar conta"}
              </Button>
            </form>

            <div className="flex items-center gap-4 my-6">
              <div className="h-px flex-1 bg-gado-line" />
              <span className="text-xs uppercase tracking-widest text-gado-muted">
                ou
              </span>
              <div className="h-px flex-1 bg-gado-line" />
            </div>

            <button
              onClick={googleLogin}
              data-testid="google-login-button"
              className="w-full flex items-center justify-center gap-3 h-11 rounded-md border border-gado-line bg-white hover:bg-gado-line/40 transition-colors font-medium text-gado-ink"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt=""
                className="h-5 w-5"
              />
              Entrar com Google
            </button>

            <p className="text-center text-sm text-gado-muted mt-8">
              {mode === "login" ? "Não tem conta?" : "Já tem conta?"}{" "}
              <button
                data-testid="toggle-mode-button"
                onClick={() => setMode(mode === "login" ? "register" : "login")}
                className="text-gado-green font-semibold hover:underline"
              >
                {mode === "login" ? "Cadastre-se" : "Entrar"}
              </button>
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right — image */}
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-gado-green/90 via-gado-green/30 to-transparent" />
        <div className="absolute bottom-0 p-14">
          <p className="font-cormorant text-white text-4xl xl:text-5xl leading-tight max-w-md">
            Gestão Inteligente de <span className="italic text-gado-goldLight">Pesagens.</span>
          </p>
        </div>
      </div>
    </div>
  );
}
